import { useState, useCallback } from 'react';

/**
 * Идентификаторы всех 19 колонок журнала — в том же порядке,
 * что и grid-template-columns в constants.ts.
 */
export type ColumnId =
  | 'idx'
  | 'pharmacy'
  | 'region'
  | 'name'
  | 'producer'
  | 'expiry'
  | 'serial'
  | 'qty'
  | 'qtyPcs'
  | 'price'
  | 'totalSum'
  | 'manager'
  | 'category'
  | 'priceGroup'
  | 'code'
  | 'barcode'
  | 'rxotc'
  | 'comment'
  | 'recordDate'
  | 'createdBy';

export interface ColumnDef {
  id: ColumnId;
  label: string;
  /** Нельзя скрыть — всегда отображается */
  required?: boolean;
  /** CSS-ширина для grid-template-columns */
  width: string;
}

/** Мастер-список всех колонок */
export const ALL_COLUMNS: ColumnDef[] = [
  { id: 'idx', label: '№', required: true, width: '44px' },
  { id: 'pharmacy', label: 'Аптека', required: true, width: '80px' },
  { id: 'region', label: 'Регион', width: '100px' },
  { id: 'name', label: 'Наименование', required: true, width: 'minmax(160px,1.5fr)' },
  { id: 'producer', label: 'Производитель', width: 'minmax(120px,1fr)' },
  { id: 'expiry', label: 'Срок годности', width: '100px' },
  { id: 'serial', label: 'Серия', width: '96px' },
  { id: 'qty', label: 'Кол-во уп.', required: true, width: '80px' },
  { id: 'qtyPcs', label: 'Кол-во шт.', required: true, width: '80px' },
  { id: 'price', label: 'Цена', width: '90px' },
  { id: 'totalSum', label: 'Сумма', required: true, width: '100px' },
  { id: 'manager', label: 'Менеджер', width: '110px' },
  { id: 'category', label: 'Категория', width: '110px' },
  { id: 'priceGroup', label: 'Цен. группа', width: '110px' },
  { id: 'code', label: 'Код', width: '90px' },
  { id: 'barcode', label: 'ШК', width: '110px' },
  { id: 'rxotc', label: 'RX/OTC', width: '90px' },
  { id: 'comment', label: 'Комментарий', width: '150px' },
  { id: 'recordDate', label: 'Дата записи', width: '108px' },
  { id: 'createdBy', label: 'Кем создан', width: '100px' },
];

const LS_KEY = 'journal_visible_columns_v1';

const persist = (next: Set<ColumnId>) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(next)));
  } catch { /* ignore */ }
};

const defaultVisible = (): Set<ColumnId> => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as ColumnId[];
      if (Array.isArray(arr) && arr.length > 0) return new Set(arr);
    }
  } catch { /* ignore */ }
  // По умолчанию скрываем: Регион, Цен. группа, ШК, Кем создан
  return new Set<ColumnId>([
    'idx', 'pharmacy', 'name', 'producer',
    'expiry', 'serial', 'qty', 'price', 'totalSum',
    'manager', 'category', 'code', 'comment', 'recordDate', 'qtyPcs'
  ]);
};

export interface UseColumnVisibilityReturn {
  visibleColumns: Set<ColumnId>;
  visibleDefs: ColumnDef[];
  gridTemplate: string;
  toggle: (id: ColumnId) => void;
  showAll: () => void;
  reset: () => void;
}

export const useColumnVisibility = (): UseColumnVisibilityReturn => {
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnId>>(defaultVisible);

  const toggle = useCallback((id: ColumnId) => {
    // Проверяем обязательность ДО вызова setState — чисто, без side-effect внутри updater
    const col = ALL_COLUMNS.find(c => c.id === id);
    if (col?.required) return;

    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // persist вызывается внутри updater — это приемлемо, т.к.
      // localStorage.setItem не является чистой React-мутацией
      persist(next);
      return next;
    });
  }, []);

  const showAll = useCallback(() => {
    const next = new Set<ColumnId>(ALL_COLUMNS.map(c => c.id));
    persist(next);
    setVisibleColumns(next);
  }, []);

  const reset = useCallback(() => {
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
    setVisibleColumns(defaultVisible());
  }, []);

  const visibleDefs = ALL_COLUMNS.filter(c => visibleColumns.has(c.id));
  const gridTemplate = visibleDefs.map(c => c.width).join(' ');

  return { visibleColumns, visibleDefs, gridTemplate, toggle, showAll, reset };
};
